import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import CustomSelect, { type SelectOption } from '@/components/base/CustomSelect';
import { memberPlatforms } from '@/mocks/members';
import type { Member, MemberTier, MemberStatus } from '@/types/member';
import { appMembersApi } from '@/helpers/memberApi';

interface MemberFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (member: Member) => void;
}

const platformOptions = memberPlatforms.filter((p) => p !== 'All platforms');

const tierOptions: SelectOption[] = [
  { value: 'Starter', label: 'Starter', badge: 'Tier 1' },
  { value: 'Pro', label: 'Pro', badge: 'Tier 2' },
  { value: 'Elite', label: 'Elite VIP', badge: 'VIP' },
];

const provinceOptions = [
  'Phnom Penh',
  'Siem Reap',
  'Battambang',
  'Kandal',
  'Kampot',
  'Preah Sihanouk',
  'Banteay Meanchey',
  'Kampong Cham',
  'Other',
];

export default function MemberFormModal({ open, onClose, onSave }: MemberFormModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState(platformOptions[0] || 'BloomCommerce');
  const [tier, setTier] = useState<MemberTier>('Starter');
  const [balance, setBalance] = useState('500');
  const [status, setStatus] = useState<MemberStatus>('active');
  const [phone, setPhone] = useState('');
  const [province, setProvince] = useState('Phnom Penh');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setEmail('');
    setPlatform(platformOptions[0] || 'BloomCommerce');
    setTier('Starter');
    setBalance('500');
    setStatus('active');
    setPhone('');
    setProvince('Phnom Penh');
    setError('');
    setIsSubmitting(false);
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || trimmedName.length < 2) {
      setError('Please enter a valid full name (at least 2 characters).');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    const initialPoints = Number(balance);
    if (isNaN(initialPoints) || initialPoints < 0) {
      setError('Initial points must be 0 or greater.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const newMember: Member = {
      id: `mb-${Date.now().toString().slice(-4)}`,
      name: trimmedName,
      email: trimmedEmail,
      platform,
      tier,
      balance: initialPoints,
      lifetime: initialPoints,
      status,
      joined: new Date().toISOString().split('T')[0],
    };

    // Attempt backend sync in background or graceful fallback
    try {
      await appMembersApi.create({
        externalUserId: trimmedEmail,
        provinceCode: province,
        verificationStatus: 'verified',
        status,
      } as Record<string, unknown>).catch(() => {
        // Backend business guard / demo mode fallback
      });
    } catch {
      // Ignored for local fallback
    }

    onSave(newMember);
    setIsSubmitting(false);
    onClose();
  };

  const inputClasses =
    'w-full h-12 px-5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all shadow-xs';

  return (
    <Modal
      open={open}
      title="Add new member"

      onClose={onClose}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Full Name */}
        <div className="flex flex-col gap-2">
          <label htmlFor="member-name" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Full name <span className="text-rose-500">*</span>
          </label>
          <input
            id="member-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sophia Vance"
            className={inputClasses}
          />
        </div>

        {/* Email Address */}
        <div className="flex flex-col gap-2">
          <label htmlFor="member-email" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Email address <span className="text-rose-500">*</span>
          </label>
          <input
            id="member-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. sophia.vance@example.com"
            className={inputClasses}
          />
        </div>

        {/* Platform Selection */}
        <div className="flex flex-col gap-2">
          <label htmlFor="member-platform" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Connected platform <span className="text-rose-500">*</span>
          </label>
          <CustomSelect
            id="member-platform"
            value={platform}
            onChange={setPlatform}
            options={platformOptions}
            size="lg"
            searchable
            searchPlaceholder="Search platform..."
            className="w-full"
          />
        </div>

        {/* Tier & Initial Points (2 columns) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="member-tier" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Membership tier
            </label>
            <CustomSelect
              id="member-tier"
              value={tier}
              onChange={(v) => setTier(v as MemberTier)}
              options={tierOptions}
              size="lg"
              searchable={false}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="member-balance" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Initial points
            </label>
            <input
              id="member-balance"
              type="number"
              min={0}
              required
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="500"
              className={inputClasses}
            />
          </div>
        </div>

        {/* Status & Province (2 columns) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground-800 tracking-wide">Account status</span>
            <div className="inline-flex items-center gap-1.5 p-1.5 rounded-xl bg-background-100 border border-background-200 h-12 w-full">
              <button
                type="button"
                onClick={() => setStatus('active')}
                className={`flex-1 h-full inline-flex items-center justify-center gap-2 px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${status === 'active'
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'text-foreground-600 hover:text-foreground-950'
                  }`}
              >
                <span className={`w-2 h-2 rounded-full ${status === 'active' ? 'bg-white animate-pulse' : 'bg-foreground-400'}`}></span>
                Active
              </button>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className={`flex-1 h-full inline-flex items-center justify-center px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${status === 'idle'
                    ? 'bg-slate-700 text-white shadow-xs'
                    : 'text-foreground-600 hover:text-foreground-950'
                  }`}
              >
                Idle
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="member-province" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Province / Region
            </label>
            <CustomSelect
              id="member-province"
              value={province}
              onChange={setProvince}
              options={provinceOptions}
              size="lg"
              searchable
              searchPlaceholder="Search province..."
              className="w-full"
            />
          </div>
        </div>

        {/* Optional Phone / Mobile Number */}
        <div className="flex flex-col gap-2">
          <label htmlFor="member-phone" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Phone number <span className="text-xs text-foreground-400 font-normal">(optional)</span>
          </label>
          <input
            id="member-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. +855 12 345 678"
            className={inputClasses}
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-xs text-rose-800 animate-fade-in">
            <i className="ri-error-warning-fill text-rose-600 text-base shrink-0"></i>
            <span className="font-medium leading-relaxed">{error}</span>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex items-center justify-end gap-3 pt-5 mt-1 border-t border-background-200/80">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="h-11 px-5 rounded-xl bg-background-100 hover:bg-background-200 border border-background-200 text-sm font-medium text-foreground-700 transition-colors cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="h-11 px-6 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer flex items-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-user-add-line text-base"></i>
                Add member
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

